import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useEmployeeSessionContext } from "@/contexts/employeeSessionContext";
import { useLinkedEmployeeState, type LinkedEmployeeState } from "@/hooks/useLinkedEmployee";
import {
  buildAssessmentSessionsCacheKey,
  fetchAssessmentSessionsCached,
  invalidateAssessmentSessions,
  peekAssessmentSessions,
} from "@/lib/network/employeeDataCache";
import {
  assessmentSessionHasCapacityComplete,
  type AssessmentSessionRow,
} from "@/lib/api/assessments";
import {
  filterAssessmentSessionsForRoster,
  primaryRosterAssessmentSessionForEmployee,
} from "@/lib/assessmentRosterLink";
import { FC_SUBMITTED_EVENT } from "@/lib/network/employeeEvents";

/** Cooldown so tab focus / visibility churn does not refetch sessions on every resume. */
const VISIBILITY_REFETCH_MIN_MS = 50_000;

export type EmployeeFunctionalCapacityGateState = {
  /**
   * Capacity / pain questionnaire complete.
   * @deprecated Prefer {@link EmployeeFunctionalCapacityGateState.capacityComplete}.
   */
  fcComplete: boolean;
  /** Capacity / pain questionnaire complete (MAPS "P"). */
  capacityComplete: boolean;
  /**
   * Latched true once capacity has completed — prevents nav/cards flicker during background refresh.
   * @deprecated Prefer {@link EmployeeFunctionalCapacityGateState.capacityCompleteSticky}.
   */
  fcCompleteSticky: boolean;
  capacityCompleteSticky: boolean;
  /**
   * Full MAPS (capacity + posture + ROM + FMS). Always false in this gate — session-only.
   * Use org/employee testing progress (`fullMapsComplete`) for the physical-steps signal.
   */
  fullMapsComplete: false;
  loading: boolean;
  refresh: () => Promise<void>;
  employeeId: string | null;
  respondentEmail: string;
  sessions: AssessmentSessionRow[];
};

/**
 * Shared FC-complete signal for employee nav / gates — merges respondent + org session reads.
 *
 * - Primary loads set `loading` only when no cached sessions exist for the scope.
 * - Visibility-driven refreshes are throttled and never toggle `loading`.
 * - Failed refreshes keep last-known sessions instead of clearing UI state.
 */
const IDLE_FC_GATE: EmployeeFunctionalCapacityGateState = {
  fcComplete: false,
  capacityComplete: false,
  fcCompleteSticky: false,
  capacityCompleteSticky: false,
  fullMapsComplete: false,
  loading: false,
  refresh: async () => {},
  employeeId: null,
  respondentEmail: "",
  sessions: [],
};

export function useEmployeeFunctionalCapacityGate(
  enabled: boolean,
  orgId: string | null,
): EmployeeFunctionalCapacityGateState {
  const session = useEmployeeSessionContext();
  const skipLocal = Boolean(session);
  const linked = useLinkedEmployeeState({ disabled: skipLocal });
  const gate = useEmployeeFunctionalCapacityGateState(enabled, orgId, linked, {
    disabled: skipLocal,
  });
  if (session) return session.fcGate;
  return gate;
}

/** @internal Prefer {@link useEmployeeFunctionalCapacityGate} or {@link EmployeeSessionProvider}. */
export function useEmployeeFunctionalCapacityGateState(
  enabled: boolean,
  orgId: string | null,
  linked: LinkedEmployeeState,
  options?: { disabled?: boolean },
): EmployeeFunctionalCapacityGateState {
  const disabled = options?.disabled ?? false;
  const { authEmail } = useAuth();
  const { employeeId, linkedEmployee } = linked;
  const employeeRow = linkedEmployee;

  const respondentEmail = useMemo(
    () => employeeRow?.email?.trim() || authEmail?.trim() || "",
    [employeeRow?.email, authEmail],
  );

  const sessionCacheKey =
    enabled && orgId && employeeId
      ? buildAssessmentSessionsCacheKey(orgId, employeeId, respondentEmail)
      : "";

  const cachedSessions = sessionCacheKey ? peekAssessmentSessions(sessionCacheKey) : undefined;
  const hasCachedSessions = cachedSessions !== undefined;

  const [sessions, setSessions] = useState<AssessmentSessionRow[]>(cachedSessions ?? []);
  const [loading, setLoading] = useState(
    Boolean(enabled && orgId && employeeId && !hasCachedSessions),
  );
  const [fcCompleteSticky, setFcCompleteSticky] = useState(false);
  const lastVisibilityRefetchAt = useRef(0);
  const scopeKeyRef = useRef<string>("");

  const fetchSessions = useCallback(async (): Promise<AssessmentSessionRow[]> => {
    if (!enabled || !orgId || !employeeId) return [];
    return fetchAssessmentSessionsCached(orgId, employeeId, respondentEmail);
  }, [enabled, orgId, employeeId, respondentEmail]);

  const resetSessionsIfScopeChanged = useCallback(() => {
    const key = `${orgId ?? ""}:${employeeId ?? ""}:${respondentEmail}`;
    if (scopeKeyRef.current !== key) {
      scopeKeyRef.current = key;
      const peeked = sessionCacheKey ? peekAssessmentSessions(sessionCacheKey) : undefined;
      setSessions(peeked ?? []);
      setFcCompleteSticky(false);
    }
  }, [orgId, employeeId, respondentEmail, sessionCacheKey]);

  const reloadPrimary = useCallback(async () => {
    if (!enabled || !orgId || !employeeId) {
      setSessions([]);
      setLoading(false);
      return;
    }
    resetSessionsIfScopeChanged();
    const peeked = sessionCacheKey ? peekAssessmentSessions(sessionCacheKey) : undefined;
    if (peeked === undefined) setLoading(true);
    try {
      const rows = await fetchSessions();
      setSessions(rows);
    } catch {
      /* Keep last known sessions on soft refresh failure. */
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    orgId,
    employeeId,
    fetchSessions,
    resetSessionsIfScopeChanged,
    sessionCacheKey,
  ]);

  const reloadFromVisibility = useCallback(async () => {
    if (!enabled || !orgId || !employeeId) return;
    const now = Date.now();
    if (now - lastVisibilityRefetchAt.current < VISIBILITY_REFETCH_MIN_MS) return;
    lastVisibilityRefetchAt.current = now;
    try {
      const rows = await fetchSessions();
      setSessions(rows);
    } catch {
      /* Keep last known sessions on soft refresh failure. */
    }
  }, [enabled, orgId, employeeId, fetchSessions]);

  useEffect(() => {
    if (disabled) return;
    void reloadPrimary();
  }, [disabled, reloadPrimary]);

  useEffect(() => {
    if (disabled || !enabled) return;
    const onVis = () => {
      if (document.visibilityState === "visible") void reloadFromVisibility();
    };
    const onFcSubmitted = () => {
      if (sessionCacheKey) invalidateAssessmentSessions(sessionCacheKey);
      void reloadPrimary();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener(FC_SUBMITTED_EVENT, onFcSubmitted);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener(FC_SUBMITTED_EVENT, onFcSubmitted);
    };
  }, [disabled, enabled, reloadFromVisibility, reloadPrimary, sessionCacheKey]);

  const capacityComplete = useMemo(() => {
    if (disabled) return false;
    if (!employeeId || !employeeRow) return false;
    const scoped = filterAssessmentSessionsForRoster(sessions, [employeeRow]);
    const latest = primaryRosterAssessmentSessionForEmployee(scoped, [employeeRow], employeeId);
    return assessmentSessionHasCapacityComplete(latest);
  }, [disabled, sessions, employeeRow, employeeId]);

  useEffect(() => {
    if (disabled) return;
    if (!employeeId) {
      setFcCompleteSticky(false);
      return;
    }
    if (capacityComplete) setFcCompleteSticky(true);
  }, [disabled, employeeId, capacityComplete]);

  if (disabled) return IDLE_FC_GATE;

  return {
    fcComplete: capacityComplete,
    capacityComplete,
    fcCompleteSticky,
    capacityCompleteSticky: fcCompleteSticky,
    fullMapsComplete: false,
    loading,
    refresh: reloadPrimary,
    employeeId,
    respondentEmail,
    sessions,
  };
}
